import { Injectable } from '@angular/core';
import { FieldDisplayType } from '../components/dialogs/select-filter-new/models/field-display-type';
import { FilterMatch, FilterType, SavedFilter } from '../components/dialogs/select-filter-new/models/saved-filter';
import { FilterComparerGuids } from '../components/dialogs/select-filter-new/models/filter-comparer-guids';
import { SelectionService } from '../models/review/selection-service';
import { FilterService } from '../services/filter.service';
import { SavedFilters } from '../models/SavedFilters';
import { BehaviorSubject, Observable } from 'rxjs';
import { TextGroupManager } from '@models/review/text-group-manager';

@Injectable({
	providedIn: 'root'
})
export class BrowseManager {
	public currentFilters$: Observable<SavedFilters | SavedFilter>;
	private currentFiltersSource = new BehaviorSubject<SavedFilters | SavedFilter>(null);

	constructor(private selectionService: SelectionService, private filterService: FilterService, private textGroupManager: TextGroupManager) {
		this.currentFilters$ = this.currentFiltersSource.asObservable();
	}

	public reset(): void {
		this.currentFiltersSource.next(null);
		this.textGroupManager.clear();
	}

	public setBrowseFilter(filter: SavedFilters | SavedFilter): void {
		if (!filter) return null;
		this.currentFiltersSource.next(filter);
	}

	/** Creates a filter for the specified field with the specified value if a filter does not already exist.
	 *  If a filter already exists, field is added if field does not exist. If field exists, value is updated.
	 */
	public createOrUpdateFilter(fieldId: string, displayType: FieldDisplayType, value: string[]): SavedFilter {
		const newField = {
			fieldId: fieldId,
			displayType: displayType,
			comparer: FilterComparerGuids.Is,
			value: value
		};

		if (this.selectionService.selectedFilter) {
			const filter: SavedFilter = JSON.parse(this.selectionService.selectedFilter.filterJson);
			const match = filter.fields.find(f => f.fieldId === fieldId);
			if (match) {
				match.value = value;
			} else {
				filter.fields.push(newField);
			}

			return filter;
		}

		return {
			type: FilterType.Simple,
			match: FilterMatch.All,
			not: false,
			fields: [newField]
		};
	}

	public verifyOpenSearchQuerySyntax(query: string): string {
		if (!query) return '';

		// Test for legacy proximity search w/#.
		if (/ [wW]\/\d+ /.test(query)) {
			return 'Search appears to include legacy proximity search syntax. Use OpenSearch syntax instead, e.g. "word1 word2"~5 to find two words within five words of each other. See help for more details.';
		}

		// Test for legacy search pre/#.
		if (/ [pP][rR][eE]\/(\d)+ /.test(query)) {
			return 'Search appears to include legacy pre-proximity search syntax. This is not supported in OpenSearch. Please see help for more details.';
		}

		// Test for legacy reserved words.
		if (query.toLowerCase().includes('xfirstword') || query.toLowerCase().includes('xlastword')) {
			return 'Search appears to include legacy syntax. Use * instead of xfirstword or xlastword to retrive all documents. See help for more details.';
		}

		// Test for legacy AndAny.
		if (query.toLowerCase().includes('andany')) {
			return 'Search appears to include legacy AndAny syntax. This is not supported in OpenSearch. Please see help for more details.';
		}

		// Test for legacy numerical search character '='.
		if (query.includes('=')) {
			return 'Search appears to include legacy numerical wildcard (=). This is not supported in OpenSearch. Please see help for more details.';
		}

		// Test for legacy Regular Expressions.
		if (query.includes('##')) {
			return 'Search appears to include legacy Regular Expression syntax. Use Elastixsearch syntax instead, e.g. /regexp/. Please see help for more details.';
		}

		// Test for using symbols for boolean operations.
		if (query.includes('&') || query.includes('|') || query.includes('!')) {
			return 'If doing boolean searches, use AND OR NOT instead of & | !. Please see help for more details.';
		}

		// Warn the user if they are using legacy query syntax for a case that uses OpenSearch
		const containsLowerCaseBoolOperators = /\s+(or|and|not)\s+/.test(query)
		if (containsLowerCaseBoolOperators) {
			return 'Search appears to use boolean operators that are not all upper case. OpenSearch requires all upper case boolean operators such as AND OR NOT. Please see help for more details.';
		}


		// Test for upper case inside Regular Expressions. This will always return false since all the tokens have been converted to lower case.
		// Just changing the query to lower case would not work since boolean operators need to stay upper case.
		// Get the content of the first regular expression if it exists. Note, using matchAll would be better in case there are multiple regular expressions in the query.
		// However, matchAll is not available in the current version of TypeScript. Matching only the first regular expression seems like a sufficient solution.
		const regExParts = query.match("/(.*?)/") ?? [""];
		const regExString = regExParts.toString();
		const containsUpperCase = regExString.toLowerCase() != regExString;
		if (containsUpperCase) {
			return 'If using regular expressions, OpenSearch requires all expression characters to be lower case. Please see help for more details.';
		}

		return '';
	}
}
